import React, { Component, ErrorInfo, ReactNode } from 'react';
import { supabase } from '../../lib/supabase';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    errorMessage: string;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, errorMessage: '' };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, errorMessage: error.message };
    }

    async componentDidCatch(error: Error, info: ErrorInfo) {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            await supabase.from('error_logs').insert({
                user_id: user?.id ?? null,
                error_message: error.message,
                stack_trace: error.stack ?? '',
                path: window.location.pathname,
                metadata: {
                    componentStack: info.componentStack,
                },
            });
        } catch {
            // Ignore logging failures
        }
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) return this.props.fallback;

            return (
                <div className="min-h-[300px] flex flex-col items-center justify-center text-center p-8 gap-4">
                    <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
                        <AlertTriangle className="w-8 h-8 text-red-400" />
                    </div>
                    <div>
                        <h3 className="text-white font-bold text-lg mb-1">Coś poszło nie tak</h3>
                        <p className="text-gray-500 text-sm max-w-sm">{this.state.errorMessage || 'Wystąpił nieoczekiwany błąd. Spróbuj odświeżyć stronę.'}</p>
                    </div>
                    <button
                        onClick={() => window.location.reload()}
                        className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Odśwież stronę
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
